import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Divider,
  Snackbar,
  CircularProgress,
  Paper,
  ListItemIcon,
  Tooltip
} from '@mui/material';
import {
  School as SchoolIcon,
  CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon,
  Notifications as NotificationsIcon,
  Archive as ArchiveIcon,
  History as HistoryIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import ArchiveViewer from './ArchiveViewer';
import { 
  collection, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  addDoc,
  deleteDoc,
  writeBatch,
  query,
  where,
  serverTimestamp,
  setDoc 
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const SchoolYearManager = () => {
  const { userData } = useAuth();
  const [currentSchoolYear, setCurrentSchoolYear] = useState('');
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [migrationDialog, setMigrationDialog] = useState(false);
  const [migrationStep, setMigrationStep] = useState('');
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [archiveViewerOpen, setArchiveViewerOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [stats, setStats] = useState({
    absences: 0,
    permanences: 0,
    messages: 0
  });

  useEffect(() => {
    if (userData?.establishmentId) {
      fetchCurrentSchoolYear();
      fetchArchives();
      fetchCurrentStats();
    }
  }, [userData?.establishmentId]);

  const fetchCurrentSchoolYear = async () => {
    try {
      const establishmentDoc = await getDoc(doc(db, 'establishments', userData.establishmentId));
      if (establishmentDoc.exists()) {
        const data = establishmentDoc.data();
        const year = data.anneeScolaire || `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`;
        setCurrentSchoolYear(year);
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'année scolaire:', error);
    }
  };

  const fetchArchives = async () => {
    try {
      const archivesRef = collection(db, 'establishments', userData.establishmentId, 'archives');
      const archivesSnapshot = await getDocs(archivesRef);
      
      const archivesList = archivesSnapshot.docs.map((archiveDoc) => {
        const archiveData = archiveDoc.data();
        
        // Gérer la date de création de manière sécurisée
        let createdAtDate = new Date();
        if (archiveData.createdAt) {
          try {
            if (archiveData.createdAt.toDate) {
              createdAtDate = archiveData.createdAt.toDate();
            } else if (archiveData.createdAt instanceof Date) {
              createdAtDate = archiveData.createdAt;
            } else if (typeof archiveData.createdAt === 'string' || typeof archiveData.createdAt === 'number') {
              const parsedDate = new Date(archiveData.createdAt);
              createdAtDate = isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
            } else {
              createdAtDate = new Date();
            }
          } catch (error) {
            console.warn('Erreur lors de la conversion de la date:', error);
            createdAtDate = new Date();
          }
        }
        
        return {
          id: archiveDoc.id,
          year: archiveDoc.id,
          createdAt: createdAtDate,
          absencesCount: 0, // Sera mis à jour plus tard si nécessaire
          permanencesCount: 0,
          messagesCount: 0,
          ...archiveData
        };
      });

      setArchives(archivesList.sort((a, b) => {
        const dateA = a.createdAt && !isNaN(a.createdAt) ? a.createdAt : new Date(0);
        const dateB = b.createdAt && !isNaN(b.createdAt) ? b.createdAt : new Date(0);
        return dateB - dateA;
      }));
    } catch (error) {
      console.error('Erreur lors de la récupération des archives:', error);
      setArchives([]); // Définir une liste vide en cas d'erreur
    } finally {
      setLoading(false);
    }
  };

  const fetchCurrentStats = async () => {
    try {
      // Utiliser les collections avec l'établissement comme dans les autres composants
      const absencesQuery = query(
        collection(db, 'absences'),
        where('establishmentId', '==', userData.establishmentId)
      );
      
      const messagesQuery = query(
        collection(db, 'messages'),
        where('establishmentId', '==', userData.establishmentId)
      );

      const permanencesQuery = query(
        collection(db, 'permanences'),
        where('establishmentId', '==', userData.establishmentId)
      );

      const [absencesSnapshot, messagesSnapshot, permanencesSnapshot] = await Promise.all([
        getDocs(absencesQuery),
        getDocs(messagesQuery),
        getDocs(permanencesQuery)
      ]);

      setStats({
        absences: absencesSnapshot.size,
        permanences: permanencesSnapshot.size,
        messages: messagesSnapshot.size
      });
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      // En cas d'erreur, définir des valeurs par défaut
      setStats({
        absences: 0,
        permanences: 0,
        messages: 0
      });
    }
  };

  const getNextSchoolYear = (currentYear) => {
    const [startYear] = currentYear.split('-');
    const nextStart = parseInt(startYear) + 1;
    return `${nextStart}-${nextStart + 1}`;
  };

  const migrateCollectionInBatches = async (collectionName, archiveYear) => {
    try {
      let collectionQuery;
      
      // Pour les absences, filtrer par establishmentId
      if (collectionName === 'absences') {
        collectionQuery = query(
          collection(db, collectionName),
          where('establishmentId', '==', userData.establishmentId)
        );
      } else if (collectionName === 'permanences') {
        // Pour les permanences, filtrer par establishmentId
        collectionQuery = query(
          collection(db, collectionName),
          where('establishmentId', '==', userData.establishmentId)
        );
      } else {
        // Pour les messages, filtrer par establishmentId
        collectionQuery = query(
          collection(db, collectionName),
          where('establishmentId', '==', userData.establishmentId)
        );
      }
      
      const snapshot = await getDocs(collectionQuery);
      
      if (snapshot.empty) {
        return 0;
      }
      
      let migrated = 0;
      const batchSize = 400; // Réduire la taille pour éviter les limites
      
      for (let i = 0; i < snapshot.docs.length; i += batchSize) {
        const batch = writeBatch(db);
        const batchDocs = snapshot.docs.slice(i, i + batchSize);
        
        for (const docSnapshot of batchDocs) {
          try {
            // Créer le document dans les archives
            const archiveRef = doc(
              db, 
              'establishments', 
              userData.establishmentId, 
              'archives', 
              archiveYear, 
              collectionName, 
              docSnapshot.id
            );
            
            batch.set(archiveRef, {
              ...docSnapshot.data(),
              archivedAt: serverTimestamp()
            });
            
            // Supprimer le document original
            batch.delete(docSnapshot.ref);
            
            migrated++;
          } catch (docError) {
            console.warn(`Erreur sur le document ${docSnapshot.id}:`, docError);
          }
        }
        
        await batch.commit();
      }
      
      return migrated;
    } catch (error) {
      console.error(`Erreur lors de la migration de ${collectionName}:`, error);
      // Ne pas throw l'erreur, continuer avec les autres collections
      return 0;
    }
  };

  const handleNewSchoolYear = async () => {
    if (!currentSchoolYear) return;
    
    setMigrating(true);
    setMigrationProgress(0);
    
    try {
      const nextYear = getNextSchoolYear(currentSchoolYear);
      
      // Étape 1: Créer l'archive metadata
      setMigrationStep('Création de l\'archive...');
      setMigrationProgress(10);
      
      const archiveRef = doc(db, 'establishments', userData.establishmentId, 'archives', currentSchoolYear);
      await setDoc(archiveRef, {
        year: currentSchoolYear,
        createdAt: serverTimestamp(),
        migratedBy: userData.uid,
        status: 'completed'
      });
      
      // Étape 2: Migrer les données par batch (en plusieurs petits batches)
      setMigrationStep('Migration des absences...');
      setMigrationProgress(30);
      
      // Migrer les absences
      const absencesMigrated = await migrateCollectionInBatches('absences', currentSchoolYear);
      
      setMigrationStep('Migration des messages...');
      setMigrationProgress(60);
      
      // Migrer les messages
      const messagesMigrated = await migrateCollectionInBatches('messages', currentSchoolYear);
      
      setMigrationStep('Migration des permanences...');
      setMigrationProgress(75);
      
      // Migrer les permanences
      const permanencesMigrated = await migrateCollectionInBatches('permanences', currentSchoolYear);
      
      // Étape 3: Récupérer et sauvegarder les informations des périodes de vacance
      setMigrationStep('Sauvegarde des périodes de vacance...');
      setMigrationProgress(80);
      
      let vacancesInfo = {};
      try {
        const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
        const vacancesSnapshot = await getDocs(vacancesRef);
        
        // Sauvegarder les informations des périodes dans l'archive
        vacancesSnapshot.docs.forEach(doc => {
          const data = doc.data();
          vacancesInfo[doc.id] = {
            nom: data.nom || 'Période non définie',
            type: data.type || 'PRINTEMPS',
            debut: data.debut,
            fin: data.fin
          };
        });
        
        // Sauvegarder les informations des vacances dans l'archive metadata
        const archiveRef = doc(db, 'establishments', userData.establishmentId, 'archives', currentSchoolYear);
        await updateDoc(archiveRef, {
          vacancesInfo: vacancesInfo
        });
        
      } catch (error) {
        console.error('Erreur lors de la sauvegarde des périodes de vacance:', error);
      }
      
      // Étape 4: Supprimer les périodes de vacance existantes
      setMigrationStep('Suppression des périodes de vacance...');
      setMigrationProgress(85);
      
      try {
        const vacancesRef = collection(db, 'establishments', userData.establishmentId, 'vacances');
        const vacancesSnapshot = await getDocs(vacancesRef);
        
        const batch = writeBatch(db);
        vacancesSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        
        await batch.commit();
      } catch (error) {
        console.error('Erreur lors de la suppression des périodes de vacance:', error);
      }
      
      // Étape 4: Mettre à jour l'année scolaire
      setMigrationStep('Mise à jour de l\'année scolaire...');
      setMigrationProgress(90);
      
      const establishmentRef = doc(db, 'establishments', userData.establishmentId);
      await updateDoc(establishmentRef, {
        anneeScolaire: nextYear,
        lastMigration: serverTimestamp()
      });
      
      setMigrationProgress(100);
      setMigrationStep('Migration terminée !');
      
      // Notification de succès
      setNotification({
        open: true,
        message: `Migration vers l'année ${nextYear} terminée avec succès !`,
        severity: 'success'
      });
      
      // Rafraîchir les données
      setTimeout(() => {
        setCurrentSchoolYear(nextYear);
        fetchArchives();
        fetchCurrentStats();
        setMigrating(false);
        setMigrationDialog(false);
        setMigrationProgress(0);
        setMigrationStep('');
      }, 2000);
      
    } catch (error) {
      console.error('Erreur lors de la migration:', error);
      setMigrationStep('Erreur lors de la migration');
      setNotification({
        open: true,
        message: `Erreur lors de la migration: ${error.message}`,
        severity: 'error'
      });
      setMigrating(false);
    }
  };

  const handleViewArchive = (archive) => {
    setSelectedArchive(archive.year);
    setArchiveViewerOpen(true);
  };

  const closeNotification = () => {
    setNotification({ ...notification, open: false });
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Année courante */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SchoolIcon sx={{ mr: 2, color: 'primary.main' }} />
          <Typography variant="h6">Année Scolaire Courante</Typography>
        </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h4" color="primary" gutterBottom>
                  {currentSchoolYear}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Année scolaire active
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Données actuelles
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip 
                    label={`${stats.absences} absences`} 
                    color="warning" 
                    size="small" 
                  />
                  <Chip 
                    label={`${stats.permanences} permanences`} 
                    color="info" 
                    size="small" 
                  />
                  <Chip 
                    label={`${stats.messages} messages`} 
                    color="default" 
                    size="small" 
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<ArchiveIcon />}
            onClick={() => setMigrationDialog(true)}
            disabled={migrating}
          >
            Nouvelle Année Scolaire
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Cette action archivera toutes les données de l'année courante
          </Typography>
        </Box>
      </Paper>

      {/* Archives */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <HistoryIcon sx={{ mr: 2, color: 'secondary.main' }} />
          <Typography variant="h6">Archives</Typography>
        </Box>
        
        {archives.length === 0 ? (
          <Typography color="text.secondary">
            Aucune archive disponible
          </Typography>
        ) : (
          <List>
            {archives.map((archive) => (
              <React.Fragment key={archive.id}>
                <ListItem>
                  <ListItemIcon>
                    <ArchiveIcon color="secondary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {archive.year}
                        </Typography>
                        <Chip 
                          label="Archivé" 
                          color="success" 
                          size="small" 
                          icon={<CheckCircleIcon />} 
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                          Archivé le {(() => {
                            try {
                              if (archive.createdAt && archive.createdAt instanceof Date && !isNaN(archive.createdAt.getTime())) {
                                return format(archive.createdAt, 'dd MMMM yyyy', { locale: fr });
                              }
                              return 'Date inconnue';
                            } catch (error) {
                              console.warn('Erreur formatage date:', error);
                              return 'Date inconnue';
                            }
                          })()}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`${archive.absencesCount} absences`} 
                            size="small" 
                            variant="outlined" 
                          />
                          <Chip 
                            label={`${archive.permanencesCount} permanences`} 
                            size="small" 
                            variant="outlined" 
                          />
                          <Chip 
                            label={`${archive.messagesCount} messages`} 
                            size="small" 
                            variant="outlined" 
                          />
                        </Box>
                      </Box>
                    }
                  />
                  <Tooltip title="Consulter l'archive">
                    <IconButton 
                      onClick={() => handleViewArchive(archive)}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                </ListItem>
                <Divider />
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Dialog de confirmation de migration */}
      <Dialog
        open={migrationDialog}
        onClose={() => !migrating && setMigrationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ mr: 2, color: 'warning.main' }} />
            Nouvelle Année Scolaire
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {!migrating ? (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Cette action va archiver toutes les données de l'année courante et créer une nouvelle année scolaire.
              </Alert>
              
              <Typography variant="h6" gutterBottom>
                Année courante : {currentSchoolYear}
              </Typography>
              <Typography variant="h6" gutterBottom>
                Nouvelle année : {getNextSchoolYear(currentSchoolYear)}
              </Typography>
              
              <Typography variant="body1" sx={{ mt: 2 }}>
                Les données suivantes seront archivées :
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemText primary={`${stats.absences} absences`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`${stats.permanences} permanences`} />
                </ListItem>
                <ListItem>
                  <ListItemText primary={`${stats.messages} messages`} />
                </ListItem>
              </List>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={60} sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Migration en cours...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {migrationStep}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={migrationProgress} 
                sx={{ width: '100%' }} 
              />
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                {migrationProgress}%
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setMigrationDialog(false)}
            disabled={migrating}
          >
            Annuler
          </Button>
          <Button
            onClick={handleNewSchoolYear}
            variant="contained"
            color="primary"
            disabled={migrating}
          >
            Confirmer la Migration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Composant de consultation des archives */}
      <ArchiveViewer
        archiveYear={selectedArchive}
        open={archiveViewerOpen}
        onClose={() => setArchiveViewerOpen(false)}
      />

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={closeNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SchoolYearManager;
